import { useEffect, useRef, useState } from "react";
import { useQuota } from "../context/QuotaContext.jsx";
import OktaTreeSidebar from "../components/command/OktaTreeSidebar.jsx";
import CommandWindow from "../components/command/CommandWindow.jsx";
import ResettableErrorBoundary from "../components/common/ResettableErrorBoundary.jsx";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import ConnectOktaModal from "../components/ConnectOktaModal.jsx";
import { useAuth } from "../auth/AuthContext.jsx";
import { useUser } from "../context/UserContext.jsx";
import OktaAssessmentScopeBanner from "../components/onboarding/OktaAssessmentScopeBanner.jsx";
import OktaAppsTruncationBanner from "../components/onboarding/OktaAppsTruncationBanner.jsx";
import OktaDemoPlaybookBanner from "../components/onboarding/OktaDemoPlaybookBanner.jsx";
import {
  auth0MissingRecommendedScopes,
  isAuth0Connected,
  isAuth0Project,
  isEntraConnected,
  isOktaProject,
  isOryConnected,
  isOryProject,
  isOrySelfHostedProject,
} from "../utils/projectProvider.js";

function workspaceHasAssessment(auditData) {
  if (!auditData || typeof auditData !== "object") return false;
  const findings = auditData.findings;
  if (findings && typeof findings === "object") {
    for (const key of ["critical", "warnings", "healthy", "inconclusive", "other"]) {
      if (Array.isArray(findings[key]) && findings[key].length > 0) return true;
    }
  }
  const normalized = auditData.assessment?.findings;
  if (Array.isArray(normalized) && normalized.some((f) => f && f.status === "NOT_APPLICABLE")) {
    return true;
  }
  return auditData.summary != null && typeof auditData.summary === "object";
}

export default function Dashboard({
  idwiz,
  isEntraProject: isEntra = false,
  onOpenAuth0Connect,
  onOpenEntraConnect,
  onOpenOryConnect,
  onOpenOrySelfHostedConfig,
}) {
  const [treeOpen, setTreeOpen] = useState(false);
  const skipFirstMountEffectRef = useRef(true);
  const [connectOktaOpen, setConnectOktaOpen] = useState(false);
  const quota = useQuota();
  const quotaSendLockSecondsLeft = quota?.quotaSendLockSecondsLeft ?? 0;
  const { apiToken: sswsToken } = useOktaSession();
  const { capabilitiesUnauthorized, resetCapabilitiesAuthGate, refreshCapabilities, currentCapabilities } =
    useAuth();
  const { dr } = useUser();
  const cr = [];

  // Network debugger: log at start of render cycle (after hooks are initialized).
  // Keep it resilient: never throw while logging.
  try {
    if (import.meta?.env?.DEV) {
      // eslint-disable-next-line no-console
      console.log("[Dashboard render]", {
        sswsTokenPresent: Boolean(sswsToken),
        phase: idwiz?.phase,
        thinking: Boolean(idwiz?.thinking),
        activeWorkflow: idwiz?.activeWorkflow,
        chatHttp429Active: Boolean(idwiz?.chatHttp429Active),
        dr,
        crLen: cr.length,
        hasLocalStorage: typeof localStorage !== "undefined",
        hasSessionStorage: typeof sessionStorage !== "undefined",
      });
    }
  } catch {
    /* ignore */
  }

  useEffect(() => {
    if (!idwiz) return;
    if (skipFirstMountEffectRef.current) {
      skipFirstMountEffectRef.current = false;
      return;
    }
    idwiz.clearDashboardTabErrorState();
  }, [idwiz]);

  useEffect(() => {
    // Okta-specific: SSWS session restoration when capabilities return 401
    if (capabilitiesUnauthorized && isOktaProject(idwiz?.project)) setConnectOktaOpen(true);
  }, [capabilitiesUnauthorized, idwiz?.project]);

  if (!idwiz) {
    return (
      <div className="flex min-h-[200px] flex-1 items-center justify-center text-sm text-slate-400">
        Loading workspace…
      </div>
    );
  }

  // Okta-specific: SSWS token required before CommandWindow mounts
  if (isOktaProject(idwiz?.project) && sswsToken == null) {
    return (
      <div className="relative flex min-h-0 min-h-[520px] flex-1 flex-col bg-[#050810] text-slate-200">
        <div className="flex flex-1 items-center justify-center px-6 py-10">
          <div className="w-full max-w-xl rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-200/90">Connect Okta</p>
            <p className="mt-2 text-sm text-slate-300">
              Your SSWS token is not set. Connect Okta to continue.
            </p>
            <button
              type="button"
              onClick={() => setConnectOktaOpen(true)}
              className="mt-4 w-full rounded-lg bg-emerald-400 px-4 py-2 text-sm font-bold text-[#050810] hover:bg-emerald-300"
            >
              Connect Okta
            </button>
          </div>
        </div>
        <ConnectOktaModal
          open={connectOktaOpen}
          onClose={() => setConnectOktaOpen(false)}
          onConnectSuccess={() => {
            resetCapabilitiesAuthGate();
            refreshCapabilities();
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 min-h-[520px] flex-1 flex-col bg-[#050810] text-slate-200">
      {isOktaProject(idwiz?.project) ? (
        <>
          <OktaAssessmentScopeBanner capabilities={currentCapabilities} />
          <OktaAppsTruncationBanner auditData={idwiz?.auditData} />
          <OktaDemoPlaybookBanner
            hasSnapshot={Array.isArray(idwiz?.snapshots) && idwiz.snapshots.length > 0}
          />
        </>
      ) : null}
      {isEntra ? (
        <div className="border-b border-sky-500/20 bg-sky-500/10 px-4 py-2 text-center text-xs text-sky-100/90">
          Entra assessment mode — read-only. Findings and architect consultation are available; automated tenant write
          operations are not enabled for Entra projects.
          {isEntraConnected(idwiz?.project) && typeof onOpenEntraConnect === "function" ? (
            <>
              {" "}
              <button
                type="button"
                className="font-semibold text-sky-200 underline"
                onClick={onOpenEntraConnect}
              >
                Update credentials
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {isOryProject(idwiz?.project) ? (
        <div className="border-b border-cyan-500/20 bg-cyan-500/10 px-4 py-2 text-center text-xs text-cyan-100/90">
          {isOrySelfHostedProject(idwiz?.project)
            ? "Ory (self-hosted) assessment mode — 11 security rules active on attested module scope and collected posture. "
            : "Ory Network assessment mode — 11 security rules active on collected posture. "}
          {workspaceHasAssessment(idwiz?.auditData)
            ? "Re-run assessment from the command panel for fresh data."
            : "Run assessment from the command panel after connect."}
          {isOryConnected(idwiz?.project) &&
          !isOrySelfHostedProject(idwiz?.project) &&
          typeof onOpenOryConnect === "function" ? (
            <>
              {" "}
              <button
                type="button"
                className="font-semibold text-cyan-200 underline"
                onClick={onOpenOryConnect}
              >
                Update credentials
              </button>
            </>
          ) : null}
          {isOryConnected(idwiz?.project) &&
          isOrySelfHostedProject(idwiz?.project) &&
          typeof onOpenOrySelfHostedConfig === "function" ? (
            <>
              {" "}
              <button
                type="button"
                className="font-semibold text-cyan-200 underline"
                onClick={onOpenOrySelfHostedConfig}
              >
                Update configuration / inputs
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {isAuth0Project(idwiz?.project) ? (
        <>
          <div className="border-b border-violet-500/20 bg-violet-500/10 px-4 py-2 text-center text-xs text-violet-100/90">
            Auth0 assessment mode — read-only Management API collectors are active.{" "}
            {workspaceHasAssessment(idwiz?.auditData)
              ? "Re-run assessment from the command panel for fresh data."
              : "Run assessment from the command panel."}
            {isAuth0Connected(idwiz?.project) && typeof onOpenAuth0Connect === "function" ? (
              <>
                {" "}
                <button
                  type="button"
                  className="font-semibold text-violet-200 underline"
                  onClick={onOpenAuth0Connect}
                >
                  Update M2M credentials
                </button>
              </>
            ) : null}
          </div>
          {auth0MissingRecommendedScopes(idwiz?.project).length > 0 &&
          isAuth0Connected(idwiz?.project) &&
          typeof onOpenAuth0Connect === "function" ? (
            <div className="border-b border-amber-500/20 bg-amber-500/10 px-4 py-2 text-center text-xs text-amber-100/95">
              Richer findings available — update M2M credentials to authorize 2 additional scopes (
              {auth0MissingRecommendedScopes(idwiz?.project).join(", ")}).{" "}
              <button
                type="button"
                className="font-semibold text-amber-200 underline"
                onClick={onOpenAuth0Connect}
              >
                Update credentials
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col">
        <ResettableErrorBoundary>
          {/* activeTask === "OIN_SEARCH": CommandWindow hides suggested chips + custom input; only TaskForm + Cancel */}
          <CommandWindow
              phase={idwiz.phase}
              thinking={idwiz.thinking}
              commandIntentLabel={idwiz.commandIntentLabel}
              selectedTreePath={idwiz.selectedTreePath}
              messages={idwiz.messages}
              input={idwiz.input}
              setInput={idwiz.setInput}
              sendMessage={idwiz.sendMessage}
              submitUserMessage={idwiz.submitUserMessage}
              applyNextStep={idwiz.applyNextStep}
              currentContext={idwiz.currentContext}
              conversationMemory={idwiz.conversationMemory}
              activeOptionButtons={idwiz.activeOptionButtons}
              canCreate={idwiz.canCreate}
              canMutateTab={idwiz.canMutateTab}
              canApproveExecute={idwiz.canApproveExecute}
              consultationMode={idwiz.conversationMemory?.consultationMode !== false}
              onOpenNavigation={
                isOktaProject(idwiz?.project) ? () => setTreeOpen(true) : undefined
              }
              formActive={idwiz.formActive}
              activeFormFields={idwiz.activeFormFields}
              taskFormValues={idwiz.taskFormValues}
              setTaskFormValue={idwiz.setTaskFormValue}
              currentTaskTitle={idwiz.currentTaskTitle}
              lastAssistantBrief={idwiz.lastAssistantBrief}
              manualRetryPending={idwiz.manualRetryPending}
              dailyLimitReached={idwiz.dailyLimitReached}
              sendReadyPulse={idwiz.sendReadyPulse}
              inputRef={idwiz.inputRef}
              handleInputChange={idwiz.handleInputChange}
              inputWakeBounce={idwiz.inputWakeBounce}
              onCsvFile={idwiz.handleCsvFile}
              onboardingWizard={idwiz.onboardingWizard}
              onOnboardingSelectSource={idwiz.selectOnboardingSource}
              onboardingGroupsText={idwiz.onboardingWizard?.answers?.groupsText ?? ""}
              onOnboardingGroupsTextChange={idwiz.updateOnboardingGroupsText}
              onOnboardingContinueStep2={idwiz.continueOnboardingStep2}
              onOnboardingSubmitPlan={idwiz.submitOnboardingWizardPlan}
              onExitOnboardingWizard={idwiz.exitOnboardingWizard}
              activeTask={idwiz.activeTask}
              groupRulesView={idwiz.groupRulesView}
              approveExecuteReady={idwiz.approveExecuteReady}
              approveDisabledHint={idwiz.approveDisabledHint}
              executionSubmitting={idwiz.executionSubmitting}
              onTaskExecute={idwiz.approveAndExecute}
              executionProgress={idwiz.executionProgress}
              refiningAgentResponse={idwiz.refiningAgentResponse}
              handleFormPrimaryAction={idwiz.handleFormPrimaryAction}
              taskFormUsesInlinePrimary={idwiz.taskFormUsesInlinePrimary}
              architectPrimaryActionLabel={idwiz.architectPrimaryActionLabel}
              handleOinCatalogSearchEnter={idwiz.handleOinCatalogSearchEnter}
              fetchOinResults={idwiz.fetchOinResults}
              executeOinSearch={idwiz.executeOinSearch}
              oinCatalogUi={idwiz.oinCatalogUi}
              oinCatalogSearchBusy={idwiz.oinCatalogSearchBusy}
              oinConfigureUi={idwiz.oinConfigureUi}
              oinConfigureSubmitting={idwiz.oinConfigureSubmitting}
              handleOinConfigureFieldChange={idwiz.handleOinConfigureFieldChange}
              handleOinConfigureSubmit={idwiz.handleOinConfigureSubmit}
              handleOinConfigureCancel={idwiz.handleOinConfigureCancel}
              handleOinAddToOkta={idwiz.handleOinAddToOkta}
              oinAddingAppId={idwiz.oinAddingAppId}
              showCsvButton={idwiz.showCsvButton}
              activeWorkflow={idwiz.activeWorkflow}
              chatHttp429Active={idwiz.chatHttp429Active}
              rateLimitRetrySeconds={quotaSendLockSecondsLeft}
              forceQuotaRetryNow={idwiz.forceQuotaRetryNow}
              dismissSuggestedActionSurface={idwiz.dismissSuggestedActionSurface}
              warning={idwiz.warning}
              bestPracticeViolation={idwiz.bestPracticeViolation}
              onDismissWarning={idwiz.dismissWarning}
              auditData={idwiz.auditData}
              driftData={idwiz.driftData}
              project={idwiz.project}
              isGreenfield={Boolean(idwiz.auditData?.isGreenfield ?? idwiz.project?.auditSnapshot?.isGreenfield)}
              setAuditData={typeof idwiz.setAuditData === "function" ? idwiz.setAuditData : null}
              setDriftData={typeof idwiz.setDriftData === "function" ? idwiz.setDriftData : null}
              syncAfterAssessment={
                typeof idwiz.syncAfterAssessment === "function" ? idwiz.syncAfterAssessment : null
              }
            />
        </ResettableErrorBoundary>
      </div>

      {treeOpen && isOktaProject(idwiz?.project) ? (
        <>
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
            onClick={() => setTreeOpen(false)}
          />
          <div className="fixed left-0 top-0 z-[90] flex h-full w-[min(280px,90vw)] max-w-[320px] flex-col shadow-[8px_0_40px_rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between border-b border-white/[0.06] bg-[#020617] px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Navigation</p>
              <button
                type="button"
                onClick={() => setTreeOpen(false)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-white/5 hover:text-slate-300"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <OktaTreeSidebar
                selectedPath={idwiz.selectedTreePath}
                onSelect={(path) => {
                  idwiz.setSelectedTreePath(path);
                  setTreeOpen(false);
                }}
              />
            </div>
          </div>
        </>
      ) : null}

      <ConnectOktaModal
        open={connectOktaOpen}
        onClose={() => setConnectOktaOpen(false)}
        onConnectSuccess={() => {
          resetCapabilitiesAuthGate();
          refreshCapabilities();
        }}
      />
    </div>
  );
}
